import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { StickyNote, FileEdit, CheckSquare, FolderArchive, Quote, Star, Clock, ChevronRight, Sparkles, Inbox, Mail, CalendarPlus } from "lucide-react";
import { api } from "../../lib/api";
import { WsPageTitle } from "./WorkspaceLayout";

const CARDS = [
  { to: "notlar", label: "Notlar", key: "notes_total", sub: "notes_favorite", subLabel: "favori", Icon: StickyNote, grad: "from-indigo-500 to-indigo-700" },
  { to: "yazilar", label: "Yazılarım", key: "writings_total", sub: "writings_draft", subLabel: "taslak", Icon: FileEdit, grad: "from-rose-500 to-rose-700" },
  { to: "gorevler", label: "Yapılacaklar", key: "todos_open", sub: "todos_done", subLabel: "tamamlandı", Icon: CheckSquare, grad: "from-amber-500 to-orange-600" },
  { to: "belgeler", label: "Belgelerim", key: "documents_total", sub: null, Icon: FolderArchive, grad: "from-emerald-500 to-emerald-700" },
  { to: "alintilar", label: "Alıntılarım", key: "quotes_total", sub: null, Icon: Quote, grad: "from-sky-500 to-sky-700" },
];

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

export default function WorkspaceDashboard() {
  const { data } = useQuery({
    queryKey: ["workspace", "overview"],
    queryFn: () => api.get("/admin/workspace/overview").then((r) => r.data),
  });
  const { data: inbox = [] } = useQuery({
    queryKey: ["inbox", "dashboard"],
    queryFn: () => api.get("/admin/inbox", { params: { limit: 5 } }).then((r) => r.data),
    refetchInterval: 30000,
  });
  const { data: inboxCounts } = useQuery({
    queryKey: ["inbox", "counts"],
    queryFn: () => api.get("/admin/inbox/counts").then((r) => r.data),
    refetchInterval: 30000,
  });
  const counts = data?.counts || {};

  return (
    <div data-testid="workspace-dashboard">
      <WsPageTitle
        icon={Sparkles}
        title="Hoşgeldin"
        sub="Kişisel çalışma alanın. Notların, yazıların, görevlerin ve arşivin bir arada."
      />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 mb-8">
        {/* Live inbox card gets the first slot */}
        <Link
          to="/admin/workspace/gelen-kutusu"
          className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-700 p-4 text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all group sm:col-span-2"
          data-testid="ws-card-inbox"
        >
          <div className="flex items-start justify-between">
            <Inbox size={22} className="opacity-80" />
            <span className="text-[10px] bg-white/20 border border-white/30 rounded-full px-2 py-0.5 inline-flex items-center gap-1 uppercase tracking-wider font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" />canlı
            </span>
          </div>
          <p className="text-3xl font-bold mt-3">{inboxCounts?.total ?? 0}</p>
          <p className="text-sm opacity-85 mt-0.5">Gelen Kutusu</p>
          <p className="text-xs opacity-80 mt-1 flex items-center gap-3">
            <span className="inline-flex items-center gap-1"><Mail size={11} />{inboxCounts?.unread_messages ?? 0} okunmamış</span>
            <span className="inline-flex items-center gap-1"><CalendarPlus size={11} />{inboxCounts?.pending_appointments ?? 0} bekleyen talep</span>
          </p>
        </Link>
        {CARDS.map(({ to, label, key, sub, subLabel, Icon, grad }) => (
          <Link
            key={to}
            to={`/admin/workspace/${to}`}
            className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${grad} p-4 text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all group`}
            data-testid={`ws-card-${to}`}
          >
            <div className="flex items-start justify-between">
              <Icon size={22} className="opacity-80" />
              <ChevronRight size={16} className="opacity-50 group-hover:translate-x-0.5 transition-transform" />
            </div>
            <p className="text-3xl font-bold mt-3">{counts[key] ?? 0}</p>
            <p className="text-sm opacity-85 mt-0.5">{label}</p>
            {sub && (
              <p className="text-xs opacity-70 mt-1">
                {counts[sub] ?? 0} {subLabel}
              </p>
            )}
          </Link>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <section className="bg-white rounded-2xl border border-indigo-100 p-5 shadow-sm" data-testid="ws-recent-notes">
          <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <StickyNote size={16} className="text-indigo-600" />Son notlar
            <Link to="/admin/workspace/notlar" className="ml-auto text-xs text-indigo-600 hover:underline">tümü →</Link>
          </h2>
          <ul className="space-y-1.5">
            {(data?.recent_notes || []).map((n) => (
              <li key={n.id}>
                <Link to="/admin/workspace/notlar" className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-indigo-50 group">
                  {n.favorite && <Star size={12} className="text-amber-500 fill-amber-500 shrink-0" />}
                  <span className="text-sm text-slate-800 truncate flex-1">{n.title || "(başlıksız)"}</span>
                  <span className="text-[11px] text-stone-500 shrink-0 flex items-center gap-1"><Clock size={10} />{fmtDate(n.updated_at)}</span>
                </Link>
              </li>
            ))}
            {(!data?.recent_notes || data.recent_notes.length === 0) && (
              <li className="text-sm text-stone-400 italic">Henüz not yok. İlk notunu "Notlar" bölümünden ekleyebilirsin.</li>
            )}
          </ul>
        </section>

        <section className="bg-white rounded-2xl border border-rose-100 p-5 shadow-sm" data-testid="ws-recent-writings">
          <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <FileEdit size={16} className="text-rose-600" />Son yazılar
            <Link to="/admin/workspace/yazilar" className="ml-auto text-xs text-rose-600 hover:underline">tümü →</Link>
          </h2>
          <ul className="space-y-1.5">
            {(data?.recent_writings || []).map((w) => (
              <li key={w.id}>
                <Link to="/admin/workspace/yazilar" className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-rose-50 group">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wide font-semibold ${w.status === "final" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{w.status === "final" ? "Final" : "Taslak"}</span>
                  <span className="text-sm text-slate-800 truncate flex-1">{w.title || "(başlıksız)"}</span>
                  <span className="text-[11px] text-stone-500 shrink-0">{w.word_count ?? 0} kelime</span>
                </Link>
              </li>
            ))}
            {(!data?.recent_writings || data.recent_writings.length === 0) && (
              <li className="text-sm text-stone-400 italic">Henüz yazı yok.</li>
            )}
          </ul>
        </section>

        <section className="bg-white rounded-2xl border border-violet-100 p-5 shadow-sm lg:col-span-2" data-testid="ws-recent-inbox">
          <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Inbox size={16} className="text-violet-600" />Son gelenler
            <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1 uppercase tracking-widest font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />canlı
            </span>
            <Link to="/admin/workspace/gelen-kutusu" className="ml-auto text-xs text-violet-600 hover:underline">tümü →</Link>
          </h2>
          <ul className="space-y-1.5">
            {inbox.slice(0, 5).map((it) => {
              const Icon = it.kind === "message" ? Mail : CalendarPlus;
              const color = it.kind === "message" ? "text-sky-600" : "text-rose-600";
              const link = it.kind === "message" ? "/admin/workspace/mesajlar" : "/admin/workspace/talepler";
              return (
                <li key={`${it.kind}:${it.id}`}>
                  <Link to={link} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-violet-50 group">
                    <Icon size={13} className={`${color} shrink-0`} />
                    <span className="text-sm font-medium truncate flex-1">{it.name}</span>
                    <span className="text-[11px] text-stone-500 truncate hidden sm:inline flex-1">{it.subject || it.preview}</span>
                    <span className="text-[11px] text-stone-500 shrink-0">{new Date(it.created_at).toLocaleDateString("tr-TR")}</span>
                  </Link>
                </li>
              );
            })}
            {inbox.length === 0 && <li className="text-sm text-stone-400 italic">Henüz gelen yok.</li>}
          </ul>
        </section>

        <section className="bg-white rounded-2xl border border-amber-100 p-5 shadow-sm lg:col-span-2" data-testid="ws-open-todos">
          <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <CheckSquare size={16} className="text-amber-600" />Açık görevler
            <Link to="/admin/workspace/gorevler" className="ml-auto text-xs text-amber-700 hover:underline">tümü →</Link>
          </h2>
          <ul className="space-y-1.5">
            {(data?.open_todos || []).map((t) => (
              <li key={t.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-amber-50">
                <span className={`w-2 h-2 rounded-full shrink-0 ${t.priority === "high" ? "bg-red-500" : t.priority === "low" ? "bg-stone-300" : "bg-amber-500"}`} />
                <span className="text-sm text-slate-800 truncate flex-1">{t.text}</span>
                {t.due_date && <span className="text-[11px] text-stone-500 shrink-0">son: {t.due_date}</span>}
              </li>
            ))}
            {(!data?.open_todos || data.open_todos.length === 0) && (
              <li className="text-sm text-stone-400 italic">Açık görev yok — hepsi tamam! 🎉</li>
            )}
          </ul>
        </section>
      </div>
    </div>
  );
}
